import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import { FormControl, FormHelperText } from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateShortcutChips } from "components/DateShortcutChips";
import { DateTime } from "luxon";
import { DateShortcutType, getDateRangeShortcuts } from "util/dateRangeShortcuts";

/** Shared waste date-range picker; consumers deal only in `YYYY-MM-DD` strings. */
type WasteDateRangePickerProps = {
  startDate?: string;
  endDate?: string;
  /** Fires on OK or a shortcut chip; either side can be undefined to clear. */
  onAccept: (next: { startDate?: string; endDate?: string }) => void;
  startLabel?: string;
  endLabel?: string;
  /** Show "Select a date range to view data" when empty. Defaults to true. */
  showHelperText?: boolean;
  errorMessage?: string | null;
  minWidth?: number | string;
  shortcutType?: DateShortcutType;
};

export function WasteDateRangePicker({
  startDate,
  endDate,
  onAccept,
  startLabel = "Start Date",
  endLabel = "End Date",
  showHelperText = true,
  errorMessage,
  minWidth = 300,
  shortcutType = "HISTORICAL",
}: WasteDateRangePickerProps) {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);

  const handleAccept = (next: [DateTime | null, DateTime | null]) => {
    onAccept({
      startDate: dateToIsoString(next[0]),
      endDate: dateToIsoString(next[1]),
    });
  };

  const helperText =
    errorMessage ?? (showHelperText && !start && !end ? "Select a date range to view data" : null);

  return (
    <FormControl sx={{ minWidth }} size="small" variant="standard" error={Boolean(errorMessage)}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <DateRangePicker
          calendars={2}
          onAccept={handleAccept}
          value={[start, end]}
          localeText={{ start: startLabel, end: endLabel }}
          slots={{
            field: MultiInputDateRangeField,
            shortcuts: DateShortcutChips,
          }}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              variant: "standard",
              sx: { "& .MuiInputBase-root": { height: "40px" } },
              inputProps: {
                autoComplete: "off",
                "data-1p-ignore": "true",
                "data-lpignore": "true",
                "data-form-type": "other",
              },
              InputProps: { endAdornment: <InsertInvitationIcon /> },
            },
            shortcuts: { items: getDateRangeShortcuts(shortcutType) },
          }}
        />
      </LocalizationProvider>
      {helperText && (
        <FormHelperText role={errorMessage ? "alert" : undefined}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

function parseIsoDate(iso?: string): DateTime | null {
  if (!iso) return null;
  const d = DateTime.fromISO(iso);
  return d.isValid ? d : null;
}

function dateToIsoString(d: DateTime | null): string | undefined {
  return d?.isValid ? (d.toISODate() ?? undefined) : undefined;
}
